import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { scoreTone, isCoverageCapped } from '../tank-overview.js';
import { paramLabel } from '../../../shared/param-labels.js';
import { getDisplayUnits } from '../units.js';
import { readSalinityUnit, formatSalinityRange } from '../salinity.js';
import { toDisplayValue, displayUnit } from '../all-history.js';
import { speciesAlertFigures } from '../shown-units.js';

// Sensei Score component — replaces the old HealthGauge + bucket-average.
// Two modes:
//   Collapsed: big number, "Sensei Score" label, one-line teaser.
//               Tappable; whole card opens the sheet.
//   Expanded:  full health/stability sub-scores, AI narrative, top fix,
//               test-next, and the per-parameter breakdown drilldown.
//
// State is fetched from /api/tanks/:id/score on mount, when tank.id
// changes and when the tank's readings change. The narrative auto-loads from the same response (it's part
// of the score row) so the expanded view is instant. POST /score/narrative
// is the explicit refresh path when the user taps "Refresh narrative."
//
// Empty states:
//   - no_established_date  → ask the user to set the start date
//   - no_score_yet         → "calculating" placeholder
//   - no readings yet      → log-first nudge from the teaser

// "Based on N of M params" + a coverage caveat when the score is capped
// by missing data. The big number is grounded by being visibly partial —
// the previous design implied full coverage even when only temp was
// logged, which read as 100/All quiet on a half-tested reef.
function CoverageNote({ score, tank, expanded = false }) {
  const measured = scoredTestCount(score);
  // A quarantine tank's logged-only params, a fishless cycle's ammonia and
  // nitrite, and what the phase only logs (a cycling tank's alk) were never
  // meant to be scored, and they were logged, so they are not "the rest" to
  // go and log.
  const total = coverageTotal(score, tank);
  if (!total) return null;
  const coverage = typeof score.coverage === 'number' ? score.coverage : measured / total;
  if (measured === 0) return null;
  // Don't nag when coverage is essentially complete.
  if (coverage >= 0.85 && measured === total) return null;
  const capped =
    typeof score.coverage_cap === 'number' &&
    typeof score.health_score === 'number' &&
    score.health_score >= score.coverage_cap;
  return (
    <div
      style={{
        marginTop: expanded ? 6 : 4,
        fontSize: 13,
        color: 'var(--text-dim)',
        letterSpacing: '0.02em',
      }}
    >
      Based on {measured} of {total} test{total === 1 ? '' : 's'} the score grades
      {capped && ' — log the rest for a full score.'}
    </div>
  );
}

// How many tests the score could grade on this tank: the server's list
// (params with a weight, minus what the phase or a QT only logs and what
// the tank has no card for or its type hides) for the cards the tank has,
// plus anything already graded. It used to count only what was logged, so
// one temperature read "1 of 1 ... log the rest".
export function coverageTotal(score, tank) {
  const bd = score?.parameter_breakdown || {};
  const graded = Object.keys(bd).filter((k) => countsTowardScore(bd[k]));
  if (Array.isArray(score?.scorable_params)) {
    const cards = tank?.params && typeof tank.params === 'object' ? tank.params : null;
    const keys = new Set(graded);
    // The server's list already leaves out what the tank type hides and
    // keeps a card the keeper hid (server/score.js visibleCards).
    for (const k of score.scorable_params) {
      if (!cards || cards[k]) keys.add(k);
    }
    return keys.size;
  }
  return Object.values(bd).filter(
    (b) => b && typeof b === 'object' && !b.track_only && Number(b.weight ?? 1) > 0
  ).length;
}

// A test the score grades: an alerts-only one (a reef's ammonia, weight 0)
// is judged and alerts but never counts toward the number, so "Based on N
// of M" read one too many on both sides. A row with no weight counts, as
// the server reads it.
function countsTowardScore(b) {
  return typeof b?.score === 'number' && Number(b.weight ?? 1) > 0;
}

export function scoredTestCount(score) {
  return Object.values(score?.parameter_breakdown || {}).filter(countsTowardScore).length;
}

export function SenseiScore({ tank, onTab, onSetEstablishedDate }) {
  const [score, setScore] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');

  // A test logged on Home reloads the tank, not this card, so the card
  // refetches when the readings change, without blanking itself (#68).
  const readingsKey = useMemo(() => readingsSignature(tank.readings), [tank.readings]);
  const shownTank = useRef(null);
  const hasScore = useRef(false);
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    let alive = true;
    if (shownTank.current !== tank.id) {
      shownTank.current = tank.id;
      hasScore.current = false;
      setScore(null);
      setError('');
      setRefreshError('');
    }
    setGaveUp(false);
    api(`/api/tanks/${tank.id}/score`)
      .then((r) => {
        if (!alive || !r) return;
        hasScore.current = true;
        setScore(r);
        setError('');
        setRefreshError('');
      })
      .catch((e) => {
        // A failed refetch keeps the score already on the card.
        if (alive && !hasScore.current) setError(e.message || 'Could not load score.');
      });
    return () => {
      alive = false;
    };
  }, [tank.id, readingsKey]);

  // Sensei reads new tests once the keeper stops logging (feedback #68):
  // check back until the new read is in. After twenty minutes the card
  // stops saying so rather than promising a read forever.
  const pending = !!score?.narrative_pending && !gaveUp;
  useEffect(() => {
    if (!pending) return;
    let alive = true;
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (tries > 40) {
        clearInterval(t);
        if (alive) setGaveUp(true);
        return;
      }
      api(`/api/tanks/${tank.id}/score`)
        .then((r) => {
          if (alive && r) setScore(r);
        })
        .catch(() => {});
    }, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
    // A further test restarts the twenty minutes: a keeper typing in a
    // notebook of old tests keeps the read pending the whole time.
  }, [pending, tank.id, readingsKey]);

  // Asking for a fresh read is a write (it spends AI on the owner's
  // account), which the server refuses a viewer. A viewer reads the
  // current summary and gets no button that can only fail.
  const canRefresh = tank.role !== 'viewer';
  // A read takes 20 s or more: one that lands after the keeper switched
  // tanks belongs to the tank it was asked for, not the card on screen. A
  // failed one keeps the score on the card and says so under it.
  const refreshNarrative = async () => {
    const id = tank.id;
    setRefreshing(true);
    setRefreshError('');
    try {
      await api(`/api/tanks/${id}/score/narrative`, {
        method: 'POST',
        body: { force: false },
      });
      const r = await api(`/api/tanks/${id}/score`);
      if (shownTank.current !== id) return;
      setScore(r);
    } catch (e) {
      if (shownTank.current === id) setRefreshError(e.message || 'Could not refresh narrative.');
    } finally {
      setRefreshing(false);
    }
  };

  if (error) {
    return (
      <ScoreCard variant="muted">
        <div style={{ fontSize: 14, color: 'var(--alert)' }}>{error}</div>
      </ScoreCard>
    );
  }
  if (!score) {
    return (
      <ScoreCard variant="muted">
        <div
          className="font-heading"
          style={{
            fontSize: 13,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          Sensei Score
        </div>
        <div className="font-mono" style={{ fontSize: 31, color: 'var(--text-dim)', marginTop: 4 }}>
          —
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Loading…</div>
      </ScoreCard>
    );
  }

  if (score.empty) {
    return (
      <EmptyScore
        reason={score.reason}
        message={score.message}
        onTab={tank.role === 'viewer' ? null : onTab}
        onSetEstablishedDate={tank.role === 'viewer' ? null : onSetEstablishedDate}
      />
    );
  }

  // A number held at the coverage cap says how few tests are logged, not
  // that the tank needs attention: neutral, never the red band.
  const tone = isCoverageCapped(score)
    ? { band: 'partial', color: 'var(--text-muted)', label: 'partial' }
    : scoreTone(score.health_score);

  return (
    <>
      {/* role=button div rather than <button>: the freshness stamp
          nests a real Refresh <button>, and interactive content
          inside a native button is invalid HTML (broken a11y +
          React validateDOMNesting warnings). */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Open Sensei Score details"
        onClick={() => setExpanded(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(true);
          }
        }}
        className="slide-in span-2 sensei-score-card"
        style={{
          textAlign: 'left',
          borderRadius: 18,
          padding: '18px 20px',
          background: 'var(--surface)',
          border: '1px solid var(--surface-border)',
          boxShadow: 'var(--glass-shadow)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          alignItems: 'center',
          gap: 18,
          width: '100%',
          cursor: 'pointer',
        }}
      >
        <div
          style={{
            fontFamily: "'Oswald', 'Inter', sans-serif",
            fontWeight: 600,
            fontSize: 70.5,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: tone.color,
          }}
        >
          {score.health_score}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            className="font-heading"
            style={{
              fontSize: 13,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>Sensei Score</span>
            {(() => {
              const bd = score.parameter_breakdown || {};
              const params = Object.values(bd).filter((b) => typeof b.score === 'number');
              if (params.length === 0) return null;
              // In-range = inside the keeper-facing acceptable band, not
              // "score above 60". A value scoring 90 on the curve is still
              // off the card's band; counting it as "in range" was half
              // the bug that surfaced as "1 in range" with temp 76.4 LOW.
              const off = params.filter((b) => b.score < 100).length;
              // "N in range" counts what the score grades, as "Based on N of
              // M" below it does; an alerts-only test still counts as off.
              const inRange = params.filter(countsTowardScore).length;
              if (off === 0)
                return (
                  <span
                    className="font-mono"
                    style={{
                      fontSize: 11,
                      letterSpacing: '0.04em',
                      color: 'var(--ok, #4ade80)',
                      background: 'rgba(74, 222, 128, 0.12)',
                      padding: '2px 8px',
                      borderRadius: 999,
                      textTransform: 'none',
                    }}
                  >
                    {inRange} in range
                  </span>
                );
              return (
                <span
                  className="font-mono"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.04em',
                    color: 'var(--alert)',
                    background: 'rgba(255, 122, 122, 0.14)',
                    padding: '2px 8px',
                    borderRadius: 999,
                    textTransform: 'none',
                  }}
                >
                  {off} off
                </span>
              );
            })()}
          </div>
          <div
            style={{
              fontFamily: "'Lora', 'Noto Serif JP', serif",
              fontStyle: 'italic',
              fontSize: 16,
              lineHeight: 1.4,
              color: 'var(--text)',
            }}
          >
            {score.teaser}
          </div>
          <CoverageNote score={score} tank={tank} />
          <NarrativeFreshness
            narrativeAt={score.ai_narrative_at}
            source={score.narrative_source}
            computedAt={score.computed_at}
            hasNarrative={!!score.ai_narrative}
            pending={pending}
            onRefresh={canRefresh ? refreshNarrative : null}
            refreshing={refreshing}
          />
          {refreshError && (
            <div role="alert" style={{ fontSize: 14, color: 'var(--alert)', marginTop: 6 }}>
              {refreshError}
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <SenseiScoreSheet
          tank={tank}
          score={score}
          tone={tone}
          pending={pending}
          refreshing={refreshing}
          onRefresh={canRefresh ? refreshNarrative : null}
          onClose={() => setExpanded(false)}
        />
      )}
    </>
  );
}

function ScoreCard({ children, variant }) {
  return (
    <section
      className="slide-in span-2"
      style={{
        borderRadius: 18,
        padding: '16px 20px',
        background: variant === 'muted' ? 'transparent' : 'var(--surface)',
        border: '1px solid var(--surface-border)',
      }}
    >
      {children}
    </section>
  );
}

function EmptyScore({ reason, message, onTab, onSetEstablishedDate }) {
  // The teaser line is the brand. Keep it intact even in empty states.
  const cta =
    reason === 'no_established_date'
      ? { label: 'Set tank age', onClick: onSetEstablishedDate }
      : { label: '+ Log a reading', onClick: onTab ? () => onTab('log') : null };
  return (
    <ScoreCard>
      <div
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          marginBottom: 6,
        }}
      >
        Sensei Score
      </div>
      <div
        style={{
          fontFamily: "'Lora', 'Noto Serif JP', serif",
          fontStyle: 'italic',
          fontSize: 16,
          lineHeight: 1.5,
          color: 'var(--text)',
        }}
      >
        {message}
      </div>
      {cta.onClick && (
        <button
          type="button"
          onClick={cta.onClick}
          className="font-heading"
          style={{
            marginTop: 12,
            padding: '7px 14px',
            minHeight: 44,
            borderRadius: 999,
            color: 'var(--cyan)',
            border: '1px solid var(--accent-border)',
            fontSize: 12,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
          }}
        >
          {cta.label}
        </button>
      )}
    </ScoreCard>
  );
}

// Small "AI checked X ago" stamp under the teaser so the user can see
// at a glance whether the narrative they're reading reflects the
// latest reading or is days old. Off-band readings trigger an
// automatic refresh on the server (5-min floor + spend caps); the
// stamp keeps Sensei honest the rest of the time. A setInterval
// re-renders the component once a minute so the label advances
// while the page sits open — without it the stamp froze at first
// paint. The stale CTA is a real button wired to onRefresh, not
// just italics — clicking it triggers the same /score/narrative
// refresh the expanded sheet exposes.
function NarrativeFreshness({
  narrativeAt,
  source,
  computedAt,
  hasNarrative,
  pending = false,
  onRefresh,
  refreshing,
}) {
  const [, setTick] = useState(0);
  // A summary built from the numbers when the AI read failed has no AI
  // time; it is stamped with the score's own time and never says "AI".
  const fallback = source === 'fallback';
  const at = fallback ? computedAt : narrativeAt;
  useEffect(() => {
    if (!hasNarrative || !at) return;
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, [hasNarrative, at]);

  if (pending) {
    return (
      <div
        role="status"
        style={{
          fontSize: 14,
          marginTop: 6,
          color: 'var(--text-muted)',
        }}
      >
        {hasNarrative ? 'Re-reading your tests…' : 'Reading your tests…'}
      </div>
    );
  }
  if (!hasNarrative || !at) return null;
  const rawMs = Date.now() - new Date(at).getTime();
  if (!Number.isFinite(rawMs)) return null;
  // Clock skew between server and client can push narrativeAt into
  // the future for the client. Clamp to 0 so a fresh narrative shows
  // "just now" rather than disappearing entirely.
  const ms = Math.max(0, rawMs);
  const word = fallback ? 'Checked' : 'AI checked';
  let label;
  if (ms < 60_000) label = `${word} just now`;
  else if (ms < 3_600_000) label = `${word} ${Math.round(ms / 60_000)}m ago`;
  else if (ms < 86_400_000) label = `${word} ${Math.round(ms / 3_600_000)}h ago`;
  else label = `${word} ${Math.round(ms / 86_400_000)}d ago`;
  // A fallback offers the refresh at once: the AI read is what is missing.
  const stale = fallback || ms > 3 * 86_400_000;
  return (
    <div
      className="font-mono"
      style={{
        fontSize: 11,
        marginTop: 6,
        color: stale ? 'var(--alert)' : 'var(--text-dim)',
        letterSpacing: '0.04em',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span>{label}</span>
      {stale && onRefresh && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRefresh();
          }}
          disabled={refreshing}
          className="font-heading"
          // A 44 px target for a finger, the small pill drawn inside it;
          // the negative margin keeps the line from growing.
          style={{
            background: 'transparent',
            border: 'none',
            minHeight: 44,
            minWidth: 44,
            margin: '-14px 0',
            padding: '0 2px',
            display: 'inline-flex',
            alignItems: 'center',
            cursor: refreshing ? 'wait' : 'pointer',
            opacity: refreshing ? 0.5 : 1,
          }}
        >
          <span
            style={{
              border: '1px solid var(--alert)',
              color: 'var(--alert)',
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </span>
        </button>
      )}
    </div>
  );
}

function SenseiScoreSheet({ tank, score, tone, pending, onClose, onRefresh, refreshing }) {
  // Esc to close, body scroll lock while open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const breakdownEntries = Object.entries(score.parameter_breakdown || {}).sort(
    (a, b) => (b[1].weight || 0) - (a[1].weight || 0)
  );
  const [showBreakdown, setShowBreakdown] = useState(false);
  // The breakdown in the keeper's units, labelled the way the cards are.
  const unitOpts = { salinityUnit: readSalinityUnit(), tempUnit: getDisplayUnits(tank).temp };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ss-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(8, 20, 54, 0.72)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        animation: 'fade 0.18s ease-out',
      }}
    >
      <div
        className="scale-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 540,
          maxHeight: '92vh',
          overflowY: 'auto',
          background: 'var(--bg, #0b1736)',
          borderTop: '1px solid var(--surface-border)',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          padding: '22px 22px 28px',
          color: 'var(--text)',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div
            style={{
              fontFamily: "'Oswald', 'Inter', sans-serif",
              fontWeight: 600,
              fontSize: 61.5,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: tone.color,
            }}
          >
            {score.health_score}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="font-heading"
              style={{
                fontSize: 13,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              Sensei Score
            </div>
            <h2
              id="ss-title"
              style={{
                fontFamily: "'Lora', 'Noto Serif JP', serif",
                fontStyle: 'italic',
                fontWeight: 500,
                fontSize: 18,
                lineHeight: 1.45,
                marginTop: 4,
              }}
            >
              {score.teaser}
            </h2>
            <CoverageNote score={score} tank={tank} expanded />
            {score.is_cycling && (
              <div
                className="font-heading"
                style={{
                  marginTop: 8,
                  padding: '3px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'var(--cyan)',
                  border: '1px solid var(--accent-border)',
                  display: 'inline-block',
                }}
              >
                Cycling tank — wider targets
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              color: 'var(--text-dim)',
              fontSize: 24,
              lineHeight: 1,
              border: 'none',
              background: 'transparent',
            }}
          >
            ×
          </button>
        </header>

        <Divider />

        <Row label="Health" value={score.health_score} />
        {score.stability_score != null ? (
          <Row label="Stability" value={score.stability_score} />
        ) : (
          <RowMuted label="Stability" hint="Need 14 days of readings" />
        )}

        <Divider />

        <div
          className="font-heading"
          style={{
            fontSize: 13,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            marginBottom: 8,
          }}
        >
          What I see
        </div>
        {pending && score.ai_narrative && (
          <p
            role="status"
            style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-muted)', margin: '0 0 8px' }}
          >
            Your tests changed since this read. Sensei will read them again shortly.
          </p>
        )}
        {score.ai_narrative ? (
          <p
            style={{
              fontFamily: "'Lora', 'Noto Serif JP', serif",
              fontSize: 17,
              lineHeight: 1.6,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            {score.ai_narrative}
          </p>
        ) : (
          <p
            style={{
              fontStyle: 'italic',
              color: 'var(--text-muted)',
              fontSize: 15,
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            {pending
              ? 'Sensei is reading your tests. Check back in a minute or two.'
              : `Sensei hasn't written a read yet for this tank.${onRefresh ? ' Tap below to ask.' : ''}`}
          </p>
        )}

        {/* While the new tests are being read, a refresh would only bring
            back the read from before them. */}
        {onRefresh && !pending && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="font-heading"
            style={{
              marginTop: 12,
              minHeight: 44,
              padding: '8px 14px',
              borderRadius: 999,
              color: 'var(--cyan)',
              border: '1px solid var(--accent-border)',
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              opacity: refreshing ? 0.5 : 1,
            }}
          >
            {refreshing
              ? 'Asking Sensei…'
              : score.ai_narrative
                ? 'Refresh narrative'
                : 'Ask Sensei'}
          </button>
        )}

        {Array.isArray(score.species_alerts) && score.species_alerts.length > 0 && (
          <SpeciesAlerts alerts={score.species_alerts} tempUnit={unitOpts.tempUnit} />
        )}

        {(score.ai_top_fix || score.ai_test_next) && <Divider />}

        {/* The fix and next test belong to the read from before the tests
            changed: a "raise alk" left there reads like an order to dose
            again. Labelled and dimmed until Sensei has read them. */}
        {pending && (score.ai_top_fix || score.ai_test_next) && (
          <div style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 4px' }}>
            From the read before your tests changed:
          </div>
        )}
        <div style={pending ? { opacity: 0.55 } : undefined}>
          {score.ai_top_fix && <KvRow label="Top fix" value={score.ai_top_fix} />}
          {score.ai_test_next && <KvRow label="Test next" value={score.ai_test_next} mono />}
        </div>

        {breakdownEntries.length > 0 && (
          <>
            <Divider />
            <button
              type="button"
              onClick={() => setShowBreakdown((s) => !s)}
              className="font-heading"
              style={{
                minHeight: 44,
                padding: 0,
                color: 'var(--cyan)',
                fontSize: 13,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                background: 'transparent',
                border: 'none',
              }}
            >
              {showBreakdown ? 'Hide breakdown ↑' : 'View parameter breakdown ↓'}
            </button>
            {showBreakdown && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {breakdownEntries.map(([k, b]) => (
                  <div
                    key={k}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="font-heading"
                        style={{ fontSize: 13, letterSpacing: '0.04em' }}
                      >
                        {paramLabel(k).toUpperCase()}
                      </div>
                      <div className="font-mono" style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                        {breakdownValue(k, b, {
                          ...unitOpts,
                          cardUnit: tank?.params?.[k]?.unit,
                        })}
                        {breakdownNote(b, {
                          loggedOnly: loggedOnlyTest(score, k, b),
                        }) || breakdownRange(k, b, unitOpts)}
                        {b.using_cycling ? ' (cycling)' : ''}
                      </div>
                    </div>
                    <div
                      className="font-mono"
                      style={{ fontSize: 16, color: scoreTone(b.score).color, fontWeight: 600 }}
                    >
                      {typeof b.score === 'number' ? b.score : '–'}
                    </div>
                    <div
                      className="font-mono"
                      style={{
                        fontSize: 11,
                        color: 'var(--text-dim)',
                        minWidth: 48,
                        textAlign: 'right',
                      }}
                    >
                      {/* Ammonia / nitrite on a reef tank: they alert, but the
                          reef score never counted them. */}
                      {b.weight === 0 && !b.track_only
                        ? 'alerts only'
                        : `w ${Math.round(b.weight * 100)}%`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Changes when a test is added, removed or edited, so the card knows to
// fetch the score again.
export function readingsSignature(readings) {
  if (!Array.isArray(readings)) return '';
  let h = 0;
  let newest = 0;
  for (const r of readings) {
    const k = `${r?.id ?? ''}:${r?.value ?? ''}`;
    for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) | 0;
    const t = Date.parse(r?.ts);
    if (t > newest) newest = t;
  }
  return `${readings.length}:${newest}:${h}`;
}

// A row the score set aside carries no unit of its own, so the card's
// (opts.cardUnit) stands in: "8.4 · out of date" beside "5 ppm".
export function breakdownValue(key, b, opts) {
  const v = toDisplayValue(key, b?.value, opts);
  if (v == null) return '—';
  const unit = displayUnit(key, b?.unit || opts?.cardUnit, opts);
  return `${v}${unit ? ` ${unit}` : ''}`;
}

// A weighted test the score does not grade on this tank (a quarantine
// tank's salinity, a cycling tank's alk): an old reading of one is stored
// stale before it is marked logged-only, and read "out of date" beside its
// LOGGED pill on Home.
export function loggedOnlyTest(score, key, b) {
  return (
    !!b?.stale &&
    Array.isArray(score?.scorable_params) &&
    Number(b.weight ?? 1) > 0 &&
    !score.scorable_params.includes(key)
  );
}

// Why a reading is not scored, in short forms of the words its Home card
// uses. Every one set aside read "out of date", a Trident result from
// minutes ago and a value no kit reads included. Empty for a scored reading.
export function breakdownNote(b, { loggedOnly = false } = {}) {
  if (loggedOnly) return ' · logged, not scored';
  if (b?.track_only) {
    if (b.track_reason === 'quarantine') return ' · logged, not scored in a quarantine tank';
    if (b.track_reason === 'cycling') return ' · logged while the tank cycles, not scored';
    return ' · logged, not scored in this phase';
  }
  if (!b?.stale) return '';
  if (b.stale_reason === 'implausible') return ' · needs a check, not scored';
  if (b.stale_reason === 'test_time_unknown') return ' · Trident, test time unknown, not scored';
  if (b.stale_reason === 'stuck') return ' · same value every update, not scored';
  if (b.stale_reason === 'frozen') return " · hasn't moved in days, not scored";
  return ' · out of date, not scored';
}

// The keeper's acceptable range, which every other screen shows; the
// target band is the score curve's inner edge.
export function breakdownRange(key, b, opts) {
  const band =
    Array.isArray(b?.acceptable) && b.acceptable.length === 2
      ? b.acceptable
      : Number.isFinite(b?.acceptable_low) && Number.isFinite(b?.acceptable_high)
        ? [b.acceptable_low, b.acceptable_high]
        : [b?.target_low, b?.target_high];
  if (!Number.isFinite(Number(band[0])) || !Number.isFinite(Number(band[1]))) return '';
  if (key === 'salinity')
    return ` · range ${formatSalinityRange(band[0], band[1], opts.salinityUnit)}`;
  const unit = displayUnit(key, b?.unit, opts);
  return ` · range ${toDisplayValue(key, band[0], opts)}–${toDisplayValue(key, band[1], opts)}${unit ? ` ${unit}` : ''}`;
}

function Row({ label, value }) {
  const tone = scoreTone(value);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0',
      }}
    >
      <div
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </div>
      <div className="font-mono" style={{ fontSize: 20, color: tone.color, fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}
function RowMuted({ label, hint }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0',
      }}
    >
      <div
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </div>
      <div className="font-mono" style={{ fontSize: 13, color: 'var(--text-dim)' }}>
        {hint}
      </div>
    </div>
  );
}
function SpeciesAlerts({ alerts, tempUnit }) {
  const fmt = (n) =>
    Number.isInteger(n)
      ? n
      : Number(n)
          .toFixed(2)
          .replace(/\.?0+$/, '');
  return (
    <>
      <Divider />
      <div
        className="font-heading"
        style={{
          fontSize: 13,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--alert)',
          marginBottom: 8,
        }}
      >
        Watch for your livestock
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {alerts.map((raw) => {
          const a = { ...raw, ...speciesAlertFigures(raw, { tempUnit }) };
          const species =
            a.species.length <= 2
              ? a.species.join(' and ')
              : `${a.species.slice(0, 2).join(', ')} +${a.species.length - 2} more`;
          const want =
            a.direction === 'low'
              ? `wants at least ${fmt(a.range[0])}`
              : `wants no more than ${fmt(a.range[1])}`;
          const unit = a.unit ? ` ${a.unit}` : '';
          return (
            <div
              key={`${a.param}:${a.direction}`}
              style={{
                fontSize: 15,
                lineHeight: 1.5,
                color: 'var(--text)',
                borderLeft: '2px solid var(--alert)',
                paddingLeft: 10,
              }}
            >
              <strong>
                {a.label} {a.direction === 'low' ? 'below' : 'above'} range
              </strong>{' '}
              for {species}. Reading {fmt(a.value)}
              {unit}; {want}
              {unit}.
            </div>
          );
        })}
      </div>
    </>
  );
}

function KvRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '4px 0' }}>
      <div
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          minWidth: 90,
        }}
      >
        {label}
      </div>
      <div
        className={mono ? 'font-mono' : ''}
        style={{ fontSize: 16, color: 'var(--text)', flex: 1 }}
      >
        {value}
      </div>
    </div>
  );
}
function Divider() {
  return <div style={{ height: 1, background: 'var(--surface-border)', margin: '14px 0' }} />;
}
